import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Shield, Loader2, AlertTriangle, MousePointerClick, TrendingUp, CircleDollarSign, Cpu,
  Gauge, ShieldAlert, Activity, X, ChevronRight, RefreshCw,
} from 'lucide-react';
import { fmtDateTime } from '../lib/dateFormat';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const STATUS_CLS = (s) => s === 'LIVE' ? 'bg-emerald-500/15 text-emerald-700'
  : /PENDING/.test(s) ? 'bg-amber-500/15 text-amber-700' : 'bg-muted text-muted-foreground';

const Stat = ({ icon: Icon, label, value, sub, testId, tone }) => (
  <Card className="p-4 rounded-2xl" data-testid={testId}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs"><Icon className="h-4 w-4" />{label}</div>
    <p className={`mt-1 text-2xl font-semibold tabular-nums ${tone || ''}`}>{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
  </Card>
);

const SEV_CLS = {
  critical: 'bg-red-600 text-white', high: 'bg-red-500/15 text-red-700',
  medium: 'bg-amber-500/15 text-amber-700', low: 'bg-muted text-muted-foreground',
  informational: 'bg-blue-500/15 text-blue-700',
};

// Lightweight dependency-free dual-series bar chart (estimated vs confirmed, kept separate).
const TrendChart = ({ trend }) => {
  const data = (trend || []).slice(-30);
  if (data.length === 0) return <p className="text-xs text-muted-foreground p-4">No time-series data in range.</p>;
  const max = Math.max(1, ...data.map((d) => Math.max(d.estimated, d.confirmed)));
  const W = Math.max(320, data.length * 34), H = 140, pad = 22;
  const bw = (W - pad * 2) / data.length;
  return (
    <div className="overflow-x-auto" data-testid="earnings-trend-chart">
      <svg width={W} height={H} role="img" aria-label="Earnings trend">
        {data.map((d, i) => {
          const x = pad + i * bw;
          const eh = ((d.estimated / max) * (H - pad - 16));
          const ch = ((d.confirmed / max) * (H - pad - 16));
          return (
            <g key={d.date}>
              <rect x={x + bw * 0.12} y={H - 16 - eh} width={bw * 0.34} height={eh} rx="2" className="fill-amber-400/70">
                <title>{d.date} · estimated ₹{d.estimated.toLocaleString('en-IN')}</title>
              </rect>
              <rect x={x + bw * 0.52} y={H - 16 - ch} width={bw * 0.34} height={ch} rx="2" className="fill-emerald-500">
                <title>{d.date} · confirmed ₹{d.confirmed.toLocaleString('en-IN')}</title>
              </rect>
              <text x={x + bw / 2} y={H - 4} textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 8 }}>{d.date.slice(5)}</text>
            </g>
          );
        })}
      </svg>
      <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
        <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-400/70" />Estimated (projection)</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" />Confirmed (postbacks)</span>
      </div>
    </div>
  );
};

const FILTER_FIELD = { creator: 'creator_id', itinerary: 'itinerary_id', partner: 'partner_id', campaign: 'campaign_id', destination: 'destination' };

export default function AdminSecurity() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [denied, setDenied] = useState(false);
  const [dash, setDash] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [includeDemo, setIncludeDemo] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Earnings by source
  const [groupBy, setGroupBy] = useState('creator');
  const [filters, setFilters] = useState({});
  const [earn, setEarn] = useState(null);

  // Security events feed
  const [events, setEvents] = useState(null);
  const [sevFilter, setSevFilter] = useState('all');

  useEffect(() => { document.title = 'Cost & Security | Admin'; if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);

  const loadDash = () => {
    setRefreshing(true);
    api.securityDashboard({ date_from: dateFrom, date_to: dateTo, include_demo: includeDemo })
      .then(({ data }) => setDash(data))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); })
      .finally(() => setRefreshing(false));
  };
  const loadEarn = () => {
    api.earningsBySource({ group_by: groupBy, include_demo: includeDemo, date_from: dateFrom, date_to: dateTo, ...filters })
      .then(({ data }) => setEarn(data)).catch(() => setEarn({ rows: [] }));
  };
  useEffect(() => { if (user) loadDash(); /* eslint-disable-next-line */ }, [user, includeDemo]);
  useEffect(() => { if (user) loadEarn(); /* eslint-disable-next-line */ }, [user, groupBy, includeDemo, filters]);

  const loadEvents = () => {
    api.securityEvents({ severity: sevFilter === 'all' ? '' : sevFilter, limit: 100 })
      .then(({ data }) => setEvents(data)).catch(() => setEvents({ events: [] }));
  };
  useEffect(() => { if (user) loadEvents(); /* eslint-disable-next-line */ }, [user, sevFilter]);

  const drill = (row) => {
    const f = FILTER_FIELD[groupBy];
    if (!f || row.key === '(none)') return;
    setFilters((p) => ({ ...p, [f]: row.key }));
    // advance to a sensible next level
    const next = { creator: 'itinerary', itinerary: 'offer', partner: 'vertical', campaign: 'vertical', destination: 'partner' }[groupBy];
    if (next) setGroupBy(next);
  };
  const clearFilter = (k) => setFilters((p) => { const n = { ...p }; delete n[k]; return n; });

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (denied) return <Layout><div className="max-w-2xl mx-auto py-24 text-center"><AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Admin access required</h1></div></Layout>;

  const b = dash?.business, apiM = dash?.api, llm = dash?.llm, bud = dash?.budget, prot = dash?.protection, sig = dash?.security_signals;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Shield className="h-6 w-6 text-accent" />Cost &amp; Security</h1>
          <Button variant="outline" className="rounded-xl" onClick={() => navigate('/admin')} data-testid="security-back">← Admin dashboard</Button>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Live control room over API spend, LLM cost, abuse protection and monetisation — all from real tracked data.</p>

        {/* Controls */}
        <div className="flex flex-wrap items-end gap-3 mb-6" data-testid="security-controls">
          <div><Label className="text-xs">From</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="security-date-from" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="security-date-to" /></div>
          <Button variant="outline" className="rounded-xl" onClick={() => { loadDash(); loadEarn(); }} data-testid="security-apply"><RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />Apply</Button>
          <label className="flex items-center gap-2 text-sm ml-auto"><Switch checked={includeDemo} onCheckedChange={setIncludeDemo} data-testid="security-include-demo" /><span className="text-muted-foreground">Include demo data</span></label>
        </div>

        {/* Vertical status grid */}
        <h2 className="font-display text-lg font-semibold mb-3">Vertical status</h2>
        {!dash ? <Skeleton className="h-20 w-full rounded-2xl mb-6" /> : (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-6" data-testid="vertical-status-grid">
            {dash.vertical_status.map((v) => (
              <Card key={v.vertical} className="p-3 rounded-xl" data-testid={`vstatus-${v.vertical}`}>
                <p className="text-xs font-medium capitalize">{v.vertical}</p>
                <Badge className={`mt-1 rounded-full text-[10px] ${STATUS_CLS(v.status)}`}>{v.status}</Badge>
                {v.circuit && v.circuit !== 'n/a' && <p className={`text-[10px] mt-1 ${v.circuit === 'open' ? 'text-red-600' : 'text-muted-foreground'}`}>circuit: {v.circuit}</p>}
              </Card>
            ))}
          </div>
        )}

        {/* Today's business */}
        <h2 className="font-display text-lg font-semibold mb-3">Business ({dash?.range?.date_from}{dash?.range?.date_to !== dash?.range?.date_from ? ` → ${dash?.range?.date_to}` : ''})</h2>
        {!dash ? <Skeleton className="h-28 w-full rounded-2xl mb-6" /> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <Stat icon={MousePointerClick} label="Clicks" value={b.clicks} testId="stat-clicks" />
              <Stat icon={TrendingUp} label="Estimated commission" value={money(b.estimated_commission)} sub="Projection — NOT revenue" testId="stat-estimated" tone="text-amber-600" />
              <Stat icon={CircleDollarSign} label="Confirmed commission" value={money(b.confirmed_commission)} sub={`${b.confirmed_conversions} confirmed · ${b.pending_conversions} pending`} testId="stat-confirmed" tone="text-emerald-600" />
              <Stat icon={Activity} label="EPC (confirmed)" value={money(b.epc_confirmed)} sub="per click" testId="stat-epc" />
            </div>
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              <Card className="p-4 rounded-2xl" data-testid="clicks-by-vertical">
                <p className="text-sm font-semibold mb-2">Clicks by vertical</p>
                {b.clicks_by_vertical.length === 0 ? <p className="text-xs text-muted-foreground">No clicks in range.</p> :
                  b.clicks_by_vertical.map((r) => (
                    <div key={r.key} className="flex items-center justify-between text-sm py-1 border-b border-border last:border-0">
                      <span className="capitalize">{r.key}</span>
                      <span className="text-muted-foreground tabular-nums">{r.clicks} clicks · <span className="text-amber-600">{money(r.estimated)} est</span></span>
                    </div>
                  ))}
              </Card>
              <Card className="p-4 rounded-2xl" data-testid="clicks-by-provider">
                <p className="text-sm font-semibold mb-2">Clicks by provider</p>
                {b.clicks_by_provider.length === 0 ? <p className="text-xs text-muted-foreground">No clicks in range.</p> :
                  b.clicks_by_provider.map((r) => (
                    <div key={r.key} className="flex items-center justify-between text-sm py-1 border-b border-border last:border-0">
                      <span>{r.key}</span>
                      <span className="text-muted-foreground tabular-nums">{r.clicks} clicks · <span className="text-amber-600">{money(r.estimated)} est</span></span>
                    </div>
                  ))}
              </Card>
            </div>
          </>
        )}

        {/* Cost + protection */}
        {dash && (
          <>
            <h2 className="font-display text-lg font-semibold mb-3">API cost &amp; protection</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <Stat icon={Cpu} label="API calls" value={apiM.calls} sub={`${apiM.errors} errors`} testId="stat-api-calls" />
              <Stat icon={CircleDollarSign} label="Est. API cost" value={money(apiM.estimated_cost)} sub="estimate for budgeting" testId="stat-api-cost" />
              <Stat icon={Cpu} label="LLM calls" value={llm.calls} sub={`${money(llm.estimated_cost)} est`} testId="stat-llm" />
              <Stat icon={Gauge} label="Daily budget used" value={`${bud.consumed_pct}%`} sub={`${bud.global_day_used}/${bud.global_day_limit} · ${bud.global_day_remaining} left`} testId="stat-budget" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Stat icon={ShieldAlert} label="Rate-limit blocks" value={prot.rate_limit_blocks} testId="stat-rl-blocks" tone={prot.rate_limit_blocks ? 'text-red-600' : ''} />
              <Stat icon={ShieldAlert} label="Cost-guard blocks" value={prot.cost_guard_blocks} testId="stat-cg-blocks" tone={prot.cost_guard_blocks ? 'text-red-600' : ''} />
              <Stat icon={Activity} label="API error rate" value={`${sig.api_error_rate}%`} testId="stat-error-rate" tone={sig.api_error_rate > 5 ? 'text-red-600' : ''} />
              <Stat icon={ShieldAlert} label="Open circuits" value={sig.open_circuits.length} sub={sig.open_circuits.join(', ') || 'all closed'} testId="stat-circuits" tone={sig.open_circuits.length ? 'text-red-600' : 'text-emerald-600'} />
            </div>
          </>
        )}

        {/* Earnings by source */}
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <h2 className="font-display text-lg font-semibold">Earnings by source</h2>
          <Select value={groupBy} onValueChange={(v) => setGroupBy(v)}>
            <SelectTrigger className="w-44" data-testid="earnings-groupby"><SelectValue /></SelectTrigger>
            <SelectContent>{(earn?.drill_levels || ['creator', 'itinerary', 'day', 'offer', 'product', 'destination', 'partner', 'campaign', 'vertical']).map((l) => <SelectItem key={l} value={l}>By {l}</SelectItem>)}</SelectContent>
          </Select>
          {Object.entries(filters).map(([k, val]) => (
            <Badge key={k} variant="outline" className="rounded-full gap-1" data-testid={`earnings-filter-${k}`}>{k}: {val}<button onClick={() => clearFilter(k)}><X className="h-3 w-3" /></button></Badge>
          ))}
        </div>
        <Card className="rounded-2xl overflow-hidden" data-testid="earnings-table">
          {earn?.trend && earn.trend.length > 0 && (
            <div className="p-4 border-b border-border">
              <p className="text-sm font-semibold mb-2">Estimated vs Confirmed — daily trend</p>
              <TrendChart trend={earn.trend} />
            </div>
          )}
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-secondary/50">
            <span className="col-span-4">{groupBy}</span>
            <span className="col-span-2 text-right">Clicks</span>
            <span className="col-span-3 text-right">Estimated</span>
            <span className="col-span-3 text-right">Confirmed</span>
          </div>
          {!earn ? <div className="p-6"><Skeleton className="h-24 w-full" /></div> : earn.rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground text-center">No attributed clicks in this view.</p>
          ) : earn.rows.map((r) => {
            const drillable = !!FILTER_FIELD[groupBy] && r.key !== '(none)';
            return (
              <button key={r.key} onClick={() => drill(r)} disabled={!drillable}
                className={`w-full grid grid-cols-12 gap-2 px-4 py-2.5 text-sm border-t border-border text-left items-center ${drillable ? 'hover:bg-secondary/60 cursor-pointer' : 'cursor-default'}`}
                data-testid={`earnings-row-${r.key}`}>
                <span className="col-span-4 truncate flex items-center gap-1 font-medium">{drillable && <ChevronRight className="h-3.5 w-3.5 text-accent" />}{r.key}</span>
                <span className="col-span-2 text-right tabular-nums">{r.clicks}</span>
                <span className="col-span-3 text-right tabular-nums text-amber-600">{money(r.estimated_commission)}</span>
                <span className="col-span-3 text-right tabular-nums text-emerald-600">{money(r.confirmed_commission)} <span className="text-[10px] text-muted-foreground">({r.conversions})</span></span>
              </button>
            );
          })}
          {earn?.totals && (
            <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-sm border-t-2 border-border font-semibold bg-secondary/30" data-testid="earnings-totals">
              <span className="col-span-4">Total</span>
              <span className="col-span-2 text-right tabular-nums">{earn.totals.clicks}</span>
              <span className="col-span-3 text-right tabular-nums text-amber-600">{money(earn.totals.estimated_commission)}</span>
              <span className="col-span-3 text-right tabular-nums text-emerald-600">{money(earn.totals.confirmed_commission)}</span>
            </div>
          )}
        </Card>
        <p className="mt-3 text-[11px] text-muted-foreground flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />Estimated commission is a click-based projection and is never revenue. Confirmed commission comes solely from partner postbacks. Click a drillable row to trace creator → itinerary → offer → partner.</p>

        {/* Security Events Feed */}
        <div className="flex items-center gap-3 mt-8 mb-3 flex-wrap">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-accent" />Security events</h2>
          <Select value={sevFilter} onValueChange={setSevFilter}>
            <SelectTrigger className="w-40" data-testid="events-severity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{['all', 'critical', 'high', 'medium', 'low', 'informational'].map((s) => <SelectItem key={s} value={s}>{s === 'all' ? 'All severities' : s}</SelectItem>)}</SelectContent>
          </Select>
          {events?.by_severity && (
            <div className="flex gap-1.5 flex-wrap">
              {Object.entries(events.by_severity).map(([s, n]) => <Badge key={s} className={`rounded-full text-[10px] ${SEV_CLS[s] || ''}`}>{s}: {n}</Badge>)}
            </div>
          )}
          <Button size="sm" variant="ghost" className="rounded-lg ml-auto" onClick={loadEvents} data-testid="events-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
        <Card className="rounded-2xl overflow-hidden" data-testid="security-events-feed">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-secondary/50">
            <span className="col-span-3">Time</span><span className="col-span-2">Type</span>
            <span className="col-span-3">Endpoint</span><span className="col-span-2">Reason</span>
            <span className="col-span-1">IP</span><span className="col-span-1 text-right">Severity</span>
          </div>
          {!events ? <div className="p-6"><Skeleton className="h-24 w-full" /></div> : events.events.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground text-center" data-testid="events-empty">No security events recorded.</p>
          ) : events.events.map((e) => (
            <div key={e.event_id} className="grid grid-cols-12 gap-2 px-4 py-2 text-xs border-t border-border items-center" data-testid={`event-${e.event_id}`}>
              <span className="col-span-3 text-muted-foreground tabular-nums">{fmtDateTime(e.created_at)}</span>
              <span className="col-span-2 font-medium">{e.event_type}</span>
              <span className="col-span-3 truncate text-muted-foreground">{e.method} {e.endpoint}</span>
              <span className="col-span-2 truncate">{e.reason}{e.provider ? ` · ${e.provider}` : ''}</span>
              <span className="col-span-1 font-mono text-[10px] text-muted-foreground" title="salted hash — not raw IP">{(e.ip_hash || '').slice(0, 8)}</span>
              <span className="col-span-1 text-right"><Badge className={`rounded-full text-[10px] ${SEV_CLS[e.severity] || ''}`}>{e.severity}</Badge></span>
            </div>
          ))}
        </Card>
        <p className="mt-2 text-[11px] text-muted-foreground flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />Privacy-safe: IPs are stored only as salted short hashes; no request bodies, secrets or PII are recorded. Events auto-expire.</p>
      </div>
    </Layout>
  );
}
